import { type SxProps, type Theme } from '@mui/material';
import { MANAGER_APPLICATION_TABLE_MIN_WIDTH } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import { covWidePageContent } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';

export const managerStyles = {
  pageContent: covWidePageContent,
  reviewPageContent: () => ({
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    paddingLeft: 'clamp(12px, 2vw, 28px)',
    paddingRight: 'clamp(12px, 2vw, 28px)',
    boxSizing: 'border-box',
    minWidth: 0,
  }),
  pageDivider: (theme: Theme) => ({
    mb: { xs: theme.spacing(3), sm: theme.spacing(4) },
  }),
  tablePaper: (theme: Theme) => ({
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    borderRadius: { xs: theme.spacing(0.75), sm: theme.spacing(1) },
    overflow: 'hidden',
    border: '1px solid #dbe7f1',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  }),
  tableToolbar: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    background: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    minHeight: 50,
    paddingTop: { xs: theme.spacing(1), sm: theme.spacing(0.875) },
    paddingBottom: { xs: theme.spacing(1), sm: theme.spacing(0.875) },
    paddingLeft: { xs: theme.spacing(1.5), sm: theme.spacing(3) },
    paddingRight: { xs: theme.spacing(1.5), sm: theme.spacing(3) },
  }),
  tableToolbarContent: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    gap: theme.spacing(1),
    minHeight: '100%',
    flexWrap: 'wrap',
  }),
  tableTitle: {
    fontWeight: 700,
    color: '#334155',
    fontSize: { xs: '1rem', sm: '1.25rem' },
  } as SxProps<Theme>,
  tableContainer: {
    overflowX: 'auto',
    backgroundColor: '#ffffff',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  } as SxProps<Theme>,
  tableRoot: {
    width: '100%',
    minWidth: {
      xs: 980,
      sm: 1080,
      md: 1180,
      lg: 1300,
      xl: MANAGER_APPLICATION_TABLE_MIN_WIDTH - 120,
    },
    backgroundColor: '#ffffff',
  } as SxProps<Theme>,
  tableHead: (theme: Theme) => ({
    background: theme.palette.common.white,
    backgroundColor: 'transparent',
    boxShadow: 'none',
  }),
  tableHeaderRow: {
    borderBottom: '1px solid #dbe7f1',
  } as SxProps<Theme>,
  tableHeaderCell: (theme: Theme) => ({
    fontWeight: 'bold',
    color: theme.palette.common.black,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    backgroundColor: '#f5f9fc',
    borderBottom: '1px solid #dbe7f1',
    whiteSpace: 'nowrap',
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.25), sm: theme.spacing(1.5) },
    '&:not(:last-of-type)': {
      borderRight: '1px solid #dbe7f1',
    },
  }),
  tableHeaderFilter: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.25),
  }),
  tableBodyCell: (theme: Theme) => ({
    backgroundColor: '#ffffff',
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.25), sm: theme.spacing(1.5) },
    verticalAlign: 'top',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  }),
  tableRow: {
    '&:last-child td, &:last-child th': { border: 0 },
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.04)',
    },
    backgroundColor: '#ffffff',
  } as SxProps<Theme>,
  tablePagination: (theme: Theme) => ({
    borderTop: '1px solid #dbe7f1',
    backgroundColor: theme.palette.common.white,
  }),
  reviewPaper: (theme: Theme) => ({
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    borderRadius: theme.spacing(1),
    border: 'none',
    backgroundColor: 'common.white',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    p: 'clamp(12px, 2vw, 32px)',
    overflow: 'visible',
  }),
  reviewPageRoot: (theme: Theme) => ({
    minHeight: '100vh',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    background: theme.palette.background.default,
    paddingTop: { xs: theme.spacing(2), sm: theme.spacing(4) },
    paddingBottom: { xs: theme.spacing(2), sm: theme.spacing(4) },
    fontFamily: 'Roboto, sans-serif',
    overflowX: 'visible',
    overflowY: 'visible',
  }),
  reviewClientName: {
    fontWeight: 700,
    fontSize: { xs: '1.25rem', sm: '1.5rem' },
    color: 'text.primary',
    lineHeight: 1.2,
    textAlign: 'center',
    wordBreak: 'break-word',
  } as SxProps<Theme>,
  reviewHeaderRow: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: theme.spacing(1.5),
  }),
  reviewSubtitle: {
    fontWeight: 'bold',
    fontSize: { xs: '1rem', sm: '1rem' },
    color: '#000',
    lineHeight: 1.2,
    wordBreak: 'break-word',
  } as SxProps<Theme>,
  reviewSubtitleBox: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: theme.spacing(0.5),
    mb: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
  }),
  reviewMetaStatusRow: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap',
    columnGap: theme.spacing(2),
    overflowX: 'auto',
    width: '100%',
  }),
  reviewMetaText: {
    fontSize: { xs: '0.9375rem', sm: '1rem' },
    fontWeight: 500,
    letterSpacing: '0.01786em',
    whiteSpace: 'nowrap',
  } as SxProps<Theme>,
  reviewApplicationNumber: {
    cursor: 'help',
  } as SxProps<Theme>,
  reviewDivider: (theme: Theme) => ({
    mb: { xs: theme.spacing(3), sm: theme.spacing(4) },
  }),
  reviewTablePaper: (theme: Theme) => ({
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    p: theme.spacing(0),
    overflow: 'visible',
    borderRadius: { xs: theme.spacing(0.75), sm: theme.spacing(1) },
  }),
  reviewFooter: (theme: Theme) => ({
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: 'center',
    gap: theme.spacing(2),
    mt: { xs: theme.spacing(4), sm: theme.spacing(6) },
  }),
  reviewFooterAction: (theme: Theme) => ({
    width: { xs: '100%', sm: 'auto' },
    minHeight: 44,
    px: { xs: theme.spacing(2), sm: theme.spacing(3) },
    whiteSpace: 'normal',
    lineHeight: 1.2,
    textAlign: 'center',
  }),
  reviewSubmit: (theme: Theme) => ({
    px: theme.spacing(4),
    fontWeight: 'bold',
    borderRadius: theme.spacing(1),
    width: { xs: '100%', sm: 'auto' },
    minHeight: 44,
    whiteSpace: 'normal',
    lineHeight: 1.2,
    textAlign: 'center',
  }),
} as const;
